// src/components/ImageGallery.js
import React from "react";
import { useState, useEffect, useRef } from "react";
import Lightbox from "yet-another-react-lightbox";
import { Gallery } from "react-grid-gallery";
import "yet-another-react-lightbox/styles.css";
import { AiOutlineDownload, AiOutlineDelete } from "react-icons/ai";
import { FaRegImages, FaHeart, FaEye, FaEyeSlash, FaFolderPlus } from "react-icons/fa";
import { FiShare2 } from "react-icons/fi";
import { BsGrid } from "react-icons/bs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark } from "@fortawesome/free-solid-svg-icons";

import MasonryGallery from "./MasonryGallery";
import GallerySizeModal from "./GallerySizeModal";
import LoginPromptModal from "./LoginPromptModal";
import ShareModal from "./ShareModal";
import GuestGalleryShareModal from "./GuestGalleryShareModal";
import AlbumModal from "./AlbumModal";
import styles from "./style/Gallery.module.css"; // Import CSS Module
import pLimit from "p-limit";
import Modal from "./Modal";
import api from "../api";
import ReactGA from "react-ga4";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

const ImageGallery = ({ admin, event, images, show_delete, setImages, search=true, watermarkEnabled=false, showInteractions=false, showShare=false, onAlbumCreated, selectedAlbum=null, onAlbumChange, onAlbumImagesRemoved, allowDownload=true, allowAlbums=true, allowPhotoShare=true, featuredSection=null }) => {
  // console.log(images);
  // const [galleryImages, setImages] = useState(initialImages);

    // console.log("Image gallery  images:", images);
    // console.log("event:", event);

  const [openLBox, setOpenLBox] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [isPicModalOpen, setPicModalOpen] = useState(false);

  const controlPanelRef = useRef();
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  // pagination gallery
  const [visibleCount, setVisibleCount] = useState(100);
  const loaderRef = useRef(null);

  const navigate = useNavigate();

  // Zen Mode: hide all image overlays for a clean view
  const [zenMode, setZenMode] = useState(false);

  const { t } = useTranslation();

  // Interactions (likes + comments)
  const { authenticated, user } = useAuth();
  const [interactions, setInteractions] = useState({}); // map: image_name → {like_count, comment_count, user_liked}
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [shareModalImage, setShareModalImage] = useState(null); // image_name for the open share modal
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [guestGalleryShareImage, setGuestGalleryShareImage] = useState(null); // image_name for event wall share
  const [showOnlyLiked, setShowOnlyLiked] = useState(false);
  const [removingFromAlbum, setRemovingFromAlbum] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'selected' | 'all' }
  const [pendingAlbum, setPendingAlbum] = useState(null); // { name: string } | null — album named but not yet populated
  const [addingToPending, setAddingToPending] = useState(false);
  const overflowRef = useRef(null);

  // Gallery size (columns) — persisted to localStorage
  const getDefaultColumns = () => (window.innerWidth < 640 ? 2 : 3);
  const [galleryColumns, setGalleryColumns] = useState(() => {
    const saved = localStorage.getItem("gallery_columns");
    const val = saved ? Number(saved) : getDefaultColumns();
    const isDesktop = window.innerWidth >= 640;
    return isDesktop ? Math.max(val, 3) : val;
  });
  const [showGallerySizeModal, setShowGallerySizeModal] = useState(false);

  const handleColumnsChange = (n) => {
    setGalleryColumns(n);
    localStorage.setItem("gallery_columns", n);
  };

  // Sticky header logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyHeader(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    if (controlPanelRef.current) {
      observer.observe(controlPanelRef.current);
    }

    return () => {
      if (controlPanelRef.current) {
        observer.unobserve(controlPanelRef.current);
      }
    };
  }, []);


  // Click-outside to close overflow menu
  useEffect(() => {
    if (!overflowOpen) return;
    const handle = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target))
        setOverflowOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [overflowOpen]);

  // ⬇️ INTERSECTION OBSERVER
  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          setVisibleCount((v) => Math.min(v + 100, images.length));
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [images.length]);
  
  // Update the width dynamically on resize
  useEffect(() => {
    const handleResize = () => setContainerWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // useEffect(() => {
  //   // sort images by name
  //   images.sort((a, b) => a.name.localeCompare(b.name));
  //   console.log("images changed. total:", images.length);
  // }, [images]);

  // Fetch interaction counts (likes + comments) for all images in the event
  useEffect(() => {
    if (!showInteractions || !event?.event_id) return;
    api
      .get(`/interactions/batch?eid=${event.event_id}`)
      .then((res) => {
        const map = {};
        (res.data.interactions || []).forEach((item) => {
          map[item.image_name] = item;
        });
        setInteractions(map);
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [event?.event_id, showInteractions]);

  const handleToggleLike = async (imageName) => {
    if (!authenticated) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      const res = await api.post(
        `/interactions/like?eid=${event.event_id}&image_name=${encodeURIComponent(imageName)}`
      );
      setInteractions((prev) => ({
        ...prev,
        [imageName]: {
          ...prev[imageName],
          like_count: res.data.count,
          user_liked: res.data.liked,
        },
      }));
    } catch {
      // silently ignore
    }
  };

  const handleOpenShareModal = (imageName) => {
    if (!authenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setShareModalImage(imageName);
  };

  const handleRedirect = (path, set_id_name = true) => {
    // prevent default action
    if (path && set_id_name) {
      return () => navigate(`/${path}/${event.id_name}/`);
    } else if (path && !set_id_name) {
      return () => navigate(`/${path}`);
    } else {
      return () => navigate(`/${event.id_name}`);
    }
  };

  /**
   * return the image endpoint from his name
   * @param {string} img
   * @param {boolean} mignature
   * @returns {string}
   * */
  const img_url = (img, mignature = false, wm = false) => {
    return (
      `${process.env.REACT_APP_MODEL_ENDPOINT}/images/${img}?eid=${event.event_id}` +
      (mignature ? "&thumbnail=true" : "") +
      (wm ? "&wm=true" : "")
    );
  };

  // Handle image download
  const handleDownloadImage = async (image_url) => {
    ReactGA.event({ category: 'guest', action: 'download_photo', label: 'single' });
    try {
      const response = await api.get(image_url + "&download=1", {
        responseType: "blob", // needed to get binary data
        timeout: 10000, // optional: set request timeout
      });

      const blob = response.data; // axios stores the blob in `data`
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      // get basename of the image url and remove all get parameters of the url
      link.download = image_url.split("?")[0].split("/").pop();

      document.body.appendChild(link); // Append link to the body
      link.click(); // Simulate click to trigger download
      document.body.removeChild(link); // Clean up the DOM by removing the link after download

      // Revoke the blob URL after download to free up memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const handleDownloadZip = (selectedImages) => {
    ReactGA.event({ category: 'guest', action: 'download_photo', label: selectedImages.length === images.length ? 'all' : 'bulk' });
    const all = selectedImages.length === images.length;

    // Build payload
    const payload = {
      eid: event.event_id,
      images: selectedImages.map((img) => img.name),
      all: all,
    };

    // Create a hidden form to trigger POST directly
    const form = document.createElement("form");
    form.method = "POST";
    form.action = process.env.REACT_APP_MODEL_ENDPOINT + "/images/zip";
    form.style.display = "none";

    // Add JSON body as hidden field
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "payload";
    input.value = JSON.stringify(payload);
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const handleDeleteImage = async (image) => {
    let image_url = img_url(image.name);
    try {
      const response = await api.delete(image_url);
         // Remove from images and selectedImages
      setImages((prevImages) =>
        prevImages.filter((img) => img.name !== image.name)
      );
      setSelectedImages((prevSelected) =>
        prevSelected.filter((img) => img.name !== image.name)
      );
      return response;
    } catch (error) {
      console.error("Error deleting the image:", error);
      return false;
    }
  };

  const onRemoveFromAlbum = async () => {
    if (!selectedAlbum || selectedImages.length === 0) return;
    setRemovingFromAlbum(true);
    try {
      await api.delete(`/albums/${selectedAlbum.album_id}/images`, {
        data: { images: selectedImages.map((i) => i.name) },
        withCredentials: true,
      });
      const removedNames = selectedImages.map((i) => i.name);
      onAlbumImagesRemoved && onAlbumImagesRemoved(removedNames);
      setSelectedImages([]);
    } catch (error) {
      console.error("Error removing images from album:", error);
    } finally {
      setRemovingFromAlbum(false);
    }
  };

  const handleNeedSelection = (albumName) => {
    setPendingAlbum({ name: albumName });
    setShowAlbumModal(false);
  };

  const handleAddToPendingAlbum = async () => {
    if (!pendingAlbum || selectedImages.length === 0) return;
    setAddingToPending(true);
    try {
      await api.post(
        "/albums",
        { event_id: event.event_id, name: pendingAlbum.name, images: selectedImages.map((i) => i.name) },
        { withCredentials: true }
      );
      ReactGA.event({ category: "photographer", action: "album_create_complete", label: selectedImages.length.toString() });
      setPendingAlbum(null);
      setSelectedImages([]);
      onAlbumCreated && onAlbumCreated();
    } catch (error) {
      console.error("Error creating album:", error);
    } finally {
      setAddingToPending(false);
    }
  };

  const onDownloadSelected = () => {
    if (selectedImages.length === 0) {
      alert("Please select at least one image to download.");
      return;
    }
    // if download more than 10 images start a zip download
    if (selectedImages.length > 5) {
      handleDownloadZip(selectedImages);
      return;
    }

    selectedImages.forEach((image) => {
      let url = img_url(image.name);
      handleDownloadImage(url);
    });
  };

  const onDeleteSelected = () => {
    setDeleteConfirm({ type: "selected" });
  };

  const deleteAll = () => {
    setDeleteConfirm({ type: "all" });
  };

  const executeDelete = async () => {
    const type = deleteConfirm?.type;
    setDeleteConfirm(null);

    if (type === "selected") {
      const limit = pLimit(5);
      const deletePromises = Array.from(selectedImages).map((image) =>
        limit(() => handleDeleteImage(image))
      );
      await Promise.all(deletePromises);
      setSelectedImages([]);
    } else if (type === "all") {
      const url = `${process.env.REACT_APP_MODEL_ENDPOINT}/images?eid=${event.event_id}`;
      try {
        await fetch(url, { method: "DELETE" });
        setImages([]);
        setSelectedImages([]);
      } catch (error) {
        console.error("Error deleting images:", error);
      }
    }
  };

  // todo: if more than 10 images, get zip file
  const downloadAll = () => {
    if (!window.confirm("Are you sure you want to download all images?"))
      return;
    images.forEach((image) => {
      let url = img_url(image.name);
      handleDownloadImage(url);
    });
  };

  const randHeight = () => {
    return Math.floor(Math.random() * 100) + 200;
  };

  // Helper function to get dynamic row height
  const getDynamicRowHeight = () => {
    if (window.innerWidth < 600) return Math.min(containerWidth, 400); // Smaller height for small screens
    if (window.innerWidth < 1200) return 300; // Medium height for tablets
    return 300; // Default for larger screens
  };

  /**
   * get the correct label size according to the image size
   */
  const getImageHumanSize = (byte) => {
    if (byte < 1024) {
      return `${byte} B`;
    } else if (byte < 1024 * 1024) {
      return `${(byte / 1024).toFixed(2)} KB`;
    } else if (byte < 1024 * 1024 * 1024) {
      return `${(byte / (1024 * 1024)).toFixed(2)} MB`;
    } else {
      return `${(byte / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
  };

  // Gallery images

  // Filter by liked when toggled (client-side using fetched interaction data)
  const sourceImages = showOnlyLiked
    ? images.filter((img) => interactions[img.name]?.user_liked)
    : images;

  const toItem = (image) => ({
    original: img_url(image.name),
    src: img_url(image.name, true, watermarkEnabled),
    name: image.name,
    isSelected: selectedImages.some(img => img.name === image.name),
    width: image.width,
    height: image.height,
    caption: image.name,
    // Interaction data
    likeCount: interactions[image.name]?.like_count ?? 0,
    commentCount: interactions[image.name]?.comment_count ?? 0,
    userLiked: interactions[image.name]?.user_liked ?? false,
  });

  const IMAGES = [...sourceImages]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
    .slice(0, visibleCount) // Only take the first X images
    .map(toItem);

  // Featured album shown first (guest gallery), in album order — also kept in "All the pictures"
  const FEATURED = (() => {
    if (!featuredSection?.images?.length) return [];
    const byName = new Map(sourceImages.map((img) => [img.name, img]));
    return featuredSection.images.filter((name) => byName.has(name)).map((name) => toItem(byName.get(name)));
  })();
  const hasFeatured = FEATURED.length > 0;

  // Lightbox walks through both sections: featured first, then all pictures
  const LB_ITEMS = hasFeatured ? [...FEATURED, ...IMAGES] : IMAGES;

    // Handle select / toggle (index is relative to LB_ITEMS)
    const handleImageSelect = (index) => {
      const image = LB_ITEMS[index];
      setSelectedImages((prevSelected) => {
        const isSelected = prevSelected.some((img) => img.name === image.caption);
        if (isSelected) {
          return prevSelected.filter((img) => img.name !== image.caption);
        } else {
          return [...prevSelected, { name: image.caption }];
        }
      });
    };

    const handleDownloadClick = (index) => {
      setSelectedIndex(index);
      return handleDownloadImage(img_url(LB_ITEMS[index].name));
    };

    const handleImageClick = (index) => {
      setSelectedIndex(index);
      setOpenLBox(true);
    };

  const isSelectionMode = selectedImages.length > 0;
  const albumsEnabled = showInteractions && allowAlbums;
  // Download hidden while picking photos for a pending album, and on view-only shared albums
  const canDownload = allowDownload && !pendingAlbum;
  // Selection only makes sense when there is something to do with the selected photos
  const selectionEnabled = allowDownload || albumsEnabled || show_delete || !!selectedAlbum;
  // View-only: block the browser's save-image affordances (right-click, drag, iOS long-press)
  const protectImages = allowDownload ? {} : {
    onContextMenu: (e) => e.preventDefault(),
    onDragStart: (e) => e.preventDefault(),
    style: { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" },
  };
  const isMobile = containerWidth < 640;

  // One masonry grid; `offset` maps its local indexes onto LB_ITEMS
  const renderGrid = (items, offset) => (
    <MasonryGallery
      images={items}
      onImageClick={(i) => handleImageClick(i + offset)}
      onDownload={allowDownload ? (i) => handleDownloadClick(i + offset) : undefined}
      onSelect={selectionEnabled ? (i) => handleImageSelect(i + offset) : undefined}
      showInteractions={showInteractions}
      onToggleLike={(i) => handleToggleLike(items[i].name)}
      showShare={showShare}
      onShare={allowPhotoShare && (showInteractions || showShare) ? (i) => handleOpenShareModal(items[i].name) : undefined}
      zenMode={zenMode}
      columns={galleryColumns}
    />
  );

  const SectionDivider = ({ title, count, first = false }) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, padding: first ? "10px 6px 14px" : "36px 6px 14px" }}>
      <span style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: isMobile ? "1.3rem" : "1.55rem", fontWeight: 600,
        color: "#1a1a1a", letterSpacing: "0.3px", lineHeight: 1.2,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "70%",
      }}>
        {title}
      </span>
      <span style={{ flex: 1, height: 1, background: "#ebebeb", alignSelf: "center" }} />
      <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500, whiteSpace: "nowrap" }}>
        {t("gallery.photo_count", { count })}
      </span>
    </div>
  );

  const ToolbarBtn = ({ onClick, children, active = false, danger = false, title, iconOnly = false, disabled = false }) => (
    <button
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px",
        height: 32,
        padding: iconOnly ? "0" : "0 12px",
        width: iconOnly ? 32 : "auto",
        borderRadius: "9px",
        border: `1px solid ${danger ? "#fca5a5" : active ? "#c7d2fe" : "#ebebeb"}`,
        background: danger ? "#fff5f5" : active ? "#eef2ff" : "#fff",
        color: danger ? "#ef4444" : active ? "#6366f1" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "12px", fontWeight: 500,
        whiteSpace: "nowrap", transition: "all 0.15s",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        opacity: disabled ? 0.55 : 1,
        flexShrink: 0,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = danger ? "#fee2e2" : active ? "#e0e7ff" : "#f5f5f7"; }}
      onMouseLeave={e => { e.currentTarget.style.background = danger ? "#fff5f5" : active ? "#eef2ff" : "#fff"; }}
    >
      {children}
    </button>
  );

  const SelectionPill = ({ count, onClear }) => (
    <button
      onClick={onClear}
      title="Clear selection"
      style={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        height: 32, padding: "0 12px",
        borderRadius: "16px",
        border: "1px solid #c7d2fe",
        background: "#eef2ff",
        color: "#4f46e5",
        cursor: "pointer",
        fontSize: "12px", fontWeight: 600,
        whiteSpace: "nowrap",
        transition: "all 0.15s",
        boxShadow: "0 1px 2px rgba(99,102,241,0.10)",
        flexShrink: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "#e0e7ff"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#eef2ff"; }}
    >
      <span style={{ fontSize: "13px", lineHeight: 1 }}>×</span>
      {t("gallery.selected", { count })}
    </button>
  );

  const CountBadge = ({ count }) => (
    <span style={{
      display: "inline-flex", alignItems: "center",
      height: 28, padding: "0 10px",
      borderRadius: "14px",
      background: "#f3f4f6",
      color: "#6b7280",
      fontSize: "11px", fontWeight: 500,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}>
      {t("gallery.photo_count", { count })}
    </span>
  );

  // Shown while picking photos for an album that was just named (pendingAlbum)
  const AlbumPickHeader = ({ compact = false }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: "8px",
      padding: compact ? "2px 0" : "10px 14px",
      background: compact ? "transparent" : "#eef2ff",
      borderBottom: compact ? "none" : "1px solid #e0e7ff",
      borderRadius: compact ? 0 : "12px 12px 0 0",
      color: "#4f46e5", fontSize: "13px", fontWeight: 500,
    }}>
      <FaFolderPlus style={{ fontSize: "12px", flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {t("album.select_for_album", { name: pendingAlbum.name })}
      </span>
      <button
        onClick={() => { setPendingAlbum(null); setSelectedImages([]); }}
        title={t("album.cancel_pending")}
        aria-label={t("album.cancel_pending")}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: "none", background: "none", color: "#6366f1", cursor: "pointer", flexShrink: 0 }}
      >
        <FontAwesomeIcon icon={faXmark} style={{ fontSize: "12px" }} />
      </button>
    </div>
  );

  const OverflowMenu = ({ items }) => (
    <div ref={overflowRef} style={{ position: "relative" }}>
      <ToolbarBtn
        iconOnly
        onClick={() => setOverflowOpen(v => !v)}
        title="More options"
        active={overflowOpen}
      >
        <span style={{ fontSize: "11px", letterSpacing: "2px", lineHeight: 1, fontWeight: 300, opacity: 0.7 }}>•••</span>
      </ToolbarBtn>
      {overflowOpen && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
          minWidth: 160,
          zIndex: 1060,
          padding: "4px 0",
          overflow: "hidden",
        }}>
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => { item.onClick(); setOverflowOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                width: "100%", padding: "8px 14px",
                background: "none", border: "none",
                textAlign: "left", cursor: "pointer",
                fontSize: "12px", fontWeight: 500,
                color: item.danger ? "#ef4444" : "#374151",
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = item.danger ? "#fff5f5" : "#f9fafb"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            >
              {item.icon && <span style={{ fontSize: "12px" }}>{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ width: "100%" }}>
      {images.length > 0 ? (
        <>
          {pendingAlbum && <AlbumPickHeader />}
          {/* ── Control toolbar ── */}
          <div
            ref={controlPanelRef}
            dir="ltr"
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "10px 14px",
              background: "#fff",
              borderBottom: "1px solid #f0f0f0",
              borderRadius: pendingAlbum ? 0 : "12px 12px 0 0",
              minHeight: 54,
            }}
          >
            {/* Left group */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
              {/* AI Search — always on left, hidden in selection mode */}
              {!admin && search && !isSelectionMode && !pendingAlbum && (
                <ToolbarBtn onClick={handleRedirect("event")} title="AI Search">
                  <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                </ToolbarBtn>
              )}
              {/* Selection pill — always on left when in selection mode */}
              {isSelectionMode && (
                <SelectionPill count={selectedImages.length} onClear={() => setSelectedImages([])} />
              )}
              {/* Desktop only: full action buttons */}
              {!isMobile && !isSelectionMode && (
                <>
                  {showInteractions && authenticated && (
                    <ToolbarBtn active={showOnlyLiked} danger={showOnlyLiked} onClick={() => setShowOnlyLiked(v => !v)}>
                      <FaHeart style={{ fontSize: "11px" }} />
                      {showOnlyLiked ? t("gallery.all_photos") : t("gallery.favorites")}
                    </ToolbarBtn>
                  )}
                  {albumsEnabled && !pendingAlbum && (
                    <ToolbarBtn onClick={() => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); }}>
                      <FaFolderPlus style={{ fontSize: "11px" }} />
                      {t("album.create_album")}
                    </ToolbarBtn>
                  )}
                </>
              )}
              {!isMobile && isSelectionMode && (
                <>
                  {canDownload && (
                    <ToolbarBtn onClick={onDownloadSelected}>
                      <AiOutlineDownload style={{ fontSize: "13px" }} />
                      Download {selectedImages.length}
                    </ToolbarBtn>
                  )}
                  {albumsEnabled && pendingAlbum && (
                    <ToolbarBtn active onClick={handleAddToPendingAlbum} disabled={addingToPending || selectedImages.length === 0}>
                      <FaFolderPlus style={{ fontSize: "11px" }} />
                      {addingToPending ? t("album.saving") : t("album.add_to_album_name", { name: pendingAlbum.name, count: selectedImages.length })}
                    </ToolbarBtn>
                  )}
                  {albumsEnabled && !pendingAlbum && (
                    <ToolbarBtn onClick={() => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); }}>
                      <FaFolderPlus style={{ fontSize: "11px" }} />
                      {t("album.add_to_album")} ({selectedImages.length})
                    </ToolbarBtn>
                  )}
                  {show_delete && (
                    <ToolbarBtn danger onClick={onDeleteSelected}>
                      <AiOutlineDelete style={{ fontSize: "13px" }} />
                      Delete {selectedImages.length}
                    </ToolbarBtn>
                  )}
                  {selectedAlbum && (
                    <ToolbarBtn danger onClick={onRemoveFromAlbum} disabled={removingFromAlbum}>
                      <AiOutlineDelete style={{ fontSize: "13px" }} />
                      {removingFromAlbum ? "Removing…" : t("album.remove_from_album", { count: selectedImages.length })}
                    </ToolbarBtn>
                  )}
                </>
              )}
            </div>

            {/* Right group */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
              {isMobile ? (
                /* Mobile: overflow containing everything except AI Search */
                <OverflowMenu items={[
                  ...(isSelectionMode ? [
                    ...(canDownload ? [{ label: `Download ${selectedImages.length}`, icon: <AiOutlineDownload style={{ fontSize: "13px" }} />, onClick: onDownloadSelected }] : []),
                    ...(albumsEnabled && pendingAlbum ? [{ label: addingToPending ? t("album.saving") : t("album.add_to_album_name", { name: pendingAlbum.name, count: selectedImages.length }), icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: handleAddToPendingAlbum }] : []),
                    ...(albumsEnabled && !pendingAlbum ? [{ label: `${t("album.add_to_album")} (${selectedImages.length})`, icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); } }] : []),
                    ...(show_delete ? [{ label: `Delete ${selectedImages.length}`, icon: <AiOutlineDelete style={{ fontSize: "13px" }} />, onClick: onDeleteSelected, danger: true }] : []),
                    ...(selectedAlbum ? [{ label: removingFromAlbum ? "Removing…" : t("album.remove_from_album", { count: selectedImages.length }), icon: <AiOutlineDelete style={{ fontSize: "13px" }} />, onClick: onRemoveFromAlbum, danger: true }] : []),
                  ] : [
                    ...(showInteractions && authenticated ? [{ label: showOnlyLiked ? t("gallery.all_photos") : t("gallery.favorites"), icon: <FaHeart style={{ fontSize: "11px" }} />, onClick: () => setShowOnlyLiked(v => !v) }] : []),
                    ...(albumsEnabled && !pendingAlbum ? [{ label: t("album.create_album"), icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); } }] : []),
                    ...(albumsEnabled && pendingAlbum ? [{ label: `✕ ${pendingAlbum.name}`, icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => setPendingAlbum(null) }] : []),
                    { label: t("gallery_size.menu_item"), icon: <BsGrid style={{ fontSize: "11px" }} />, onClick: () => setShowGallerySizeModal(true) },
                  ]),
                  ...(selectedImages.length < sourceImages.length ? [{ label: t("album.select_all"), icon: <FaRegImages style={{ fontSize: "11px" }} />, onClick: () => setSelectedImages(sourceImages) }] : []),
                ]} />
              ) : (
                /* Desktop: count badge + overflow (browse) or select all (selection) */
                !isSelectionMode ? (
                  <>
                    <CountBadge count={sourceImages.length} />
                    <OverflowMenu items={[
                      ...(selectedImages.length < sourceImages.length ? [{
                        label: t("album.select_all"),
                        icon: <FaRegImages style={{ fontSize: "11px" }} />,
                        onClick: () => setSelectedImages(sourceImages),
                      }] : []),
                      { label: t("gallery_size.menu_item"), icon: <BsGrid style={{ fontSize: "11px" }} />, onClick: () => setShowGallerySizeModal(true) },
                    ]} />
                  </>
                ) : (
                  selectedImages.length < sourceImages.length && (
                    <ToolbarBtn onClick={() => setSelectedImages(sourceImages)}>
                      {t("album.select_all")}
                    </ToolbarBtn>
                  )
                )
              )}
              <ToolbarBtn
                iconOnly
                active={zenMode}
                onClick={() => setZenMode(v => !v)}
                title={zenMode ? "Exit Zen Mode" : "Zen Mode — hide overlays"}
              >
                {zenMode ? <FaEyeSlash style={{ fontSize: "12px" }} /> : <FaEye style={{ fontSize: "12px" }} />}
              </ToolbarBtn>
            </div>
          </div>

          {/* ── Sticky toolbar ── */}
          {showStickyHeader && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, zIndex: 1050,
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderBottom: "1px solid #e5e7eb",
              boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              padding: "8px 16px",
              display: "flex", flexDirection: "column", gap: "4px",
            }}>
              {/* Row 1: Event name */}
              {event?.name && (
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1rem", fontWeight: 600,
                  color: "#595959", letterSpacing: "1px",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  lineHeight: 1.3,
                }}>
                  {event.name}
                </div>
              )}

              {pendingAlbum && <AlbumPickHeader compact />}

              {/* Row 2: Actions */}
              <div dir="ltr" style={{ display: "flex", alignItems: "center", gap: "8px" }}>

              {/* Left group */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
                {!admin && search && !isSelectionMode && !pendingAlbum && (
                  <ToolbarBtn onClick={handleRedirect("event")} title="AI Search">
                    <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                  </ToolbarBtn>
                )}
                {isSelectionMode && (
                  <SelectionPill count={selectedImages.length} onClear={() => setSelectedImages([])} />
                )}
                {!isMobile && !isSelectionMode && (
                  <>
                    {showInteractions && authenticated && (
                      <ToolbarBtn active={showOnlyLiked} danger={showOnlyLiked} onClick={() => setShowOnlyLiked(v => !v)}>
                        <FaHeart style={{ fontSize: "11px" }} />
                        {showOnlyLiked ? t("gallery.all_photos") : t("gallery.favorites")}
                      </ToolbarBtn>
                    )}
                    {albumsEnabled && !pendingAlbum && (
                      <ToolbarBtn onClick={() => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); }}>
                        <FaFolderPlus style={{ fontSize: "11px" }} />
                        {t("album.create_album")}
                      </ToolbarBtn>
                    )}
                  </>
                )}
                {!isMobile && isSelectionMode && (
                  <>
                    {canDownload && (
                      <ToolbarBtn onClick={onDownloadSelected}>
                        <AiOutlineDownload style={{ fontSize: "13px" }} /> Download {selectedImages.length}
                      </ToolbarBtn>
                    )}
                    {albumsEnabled && pendingAlbum && (
                      <ToolbarBtn active onClick={handleAddToPendingAlbum} disabled={addingToPending || selectedImages.length === 0}>
                        <FaFolderPlus style={{ fontSize: "11px" }} />
                        {addingToPending ? t("album.saving") : t("album.add_to_album_name", { name: pendingAlbum.name, count: selectedImages.length })}
                      </ToolbarBtn>
                    )}
                    {albumsEnabled && !pendingAlbum && (
                      <ToolbarBtn onClick={() => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); }}>
                        <FaFolderPlus style={{ fontSize: "11px" }} />
                        {t("album.add_to_album")} ({selectedImages.length})
                      </ToolbarBtn>
                    )}
                    {show_delete && (
                      <ToolbarBtn danger onClick={onDeleteSelected}>
                        <AiOutlineDelete style={{ fontSize: "13px" }} /> Delete {selectedImages.length}
                      </ToolbarBtn>
                    )}
                    {selectedAlbum && (
                      <ToolbarBtn danger onClick={onRemoveFromAlbum} disabled={removingFromAlbum}>
                        <AiOutlineDelete style={{ fontSize: "13px" }} />
                        {removingFromAlbum ? "Removing…" : t("album.remove_from_album", { count: selectedImages.length })}
                      </ToolbarBtn>
                    )}
                  </>
                )}
              </div>

              {/* Right group */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                {isMobile ? (
                  <OverflowMenu items={[
                    ...(isSelectionMode ? [
                      ...(canDownload ? [{ label: `Download ${selectedImages.length}`, icon: <AiOutlineDownload style={{ fontSize: "13px" }} />, onClick: onDownloadSelected }] : []),
                      ...(albumsEnabled && pendingAlbum ? [{ label: addingToPending ? t("album.saving") : t("album.add_to_album_name", { name: pendingAlbum.name, count: selectedImages.length }), icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: handleAddToPendingAlbum }] : []),
                      ...(albumsEnabled && !pendingAlbum ? [{ label: `${t("album.add_to_album")} (${selectedImages.length})`, icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); } }] : []),
                      ...(show_delete ? [{ label: `Delete ${selectedImages.length}`, icon: <AiOutlineDelete style={{ fontSize: "13px" }} />, onClick: onDeleteSelected, danger: true }] : []),
                      ...(selectedAlbum ? [{ label: removingFromAlbum ? "Removing…" : t("album.remove_from_album", { count: selectedImages.length }), icon: <AiOutlineDelete style={{ fontSize: "13px" }} />, onClick: onRemoveFromAlbum, danger: true }] : []),
                    ] : [
                      ...(showInteractions && authenticated ? [{ label: showOnlyLiked ? t("gallery.all_photos") : t("gallery.favorites"), icon: <FaHeart style={{ fontSize: "11px" }} />, onClick: () => setShowOnlyLiked(v => !v) }] : []),
                      ...(albumsEnabled && !pendingAlbum ? [{ label: t("album.create_album"), icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => { if (!authenticated) { setShowLoginPrompt(true); return; } setShowAlbumModal(true); } }] : []),
                      ...(albumsEnabled && pendingAlbum ? [{ label: `✕ ${pendingAlbum.name}`, icon: <FaFolderPlus style={{ fontSize: "11px" }} />, onClick: () => setPendingAlbum(null) }] : []),
                      { label: t("gallery_size.menu_item"), icon: <BsGrid style={{ fontSize: "11px" }} />, onClick: () => setShowGallerySizeModal(true) },
                    ]),
                    ...(selectedImages.length < sourceImages.length ? [{ label: t("album.select_all"), icon: <FaRegImages style={{ fontSize: "11px" }} />, onClick: () => setSelectedImages(sourceImages) }] : []),
                  ]} />
                ) : (
                  !isSelectionMode ? (
                    <>
                      <CountBadge count={sourceImages.length} />
                      <OverflowMenu items={[
                        ...(selectedImages.length < sourceImages.length ? [{
                          label: t("album.select_all"),
                          icon: <FaRegImages style={{ fontSize: "11px" }} />,
                          onClick: () => setSelectedImages(sourceImages),
                        }] : []),
                        { label: t("gallery_size.menu_item"), icon: <BsGrid style={{ fontSize: "11px" }} />, onClick: () => setShowGallerySizeModal(true) },
                      ]} />
                    </>
                  ) : (
                    selectedImages.length < sourceImages.length && (
                      <ToolbarBtn onClick={() => setSelectedImages(sourceImages)}>
                        {t("album.select_all")}
                      </ToolbarBtn>
                    )
                  )
                )}
                <ToolbarBtn
                  iconOnly
                  active={zenMode}
                  onClick={() => setZenMode(v => !v)}
                  title={zenMode ? "Exit Zen Mode" : "Zen Mode — hide overlays"}
                >
                  {zenMode ? <FaEyeSlash style={{ fontSize: "12px" }} /> : <FaEye style={{ fontSize: "12px" }} />}
                </ToolbarBtn>
              </div>
              </div>{/* end Row 2 */}
            </div>
          )}

          {/* ── Gallery ── */}
          <div style={{ background: "#fff", borderRadius: "0 0 12px 12px", padding: "8px" }}>
            <div className={styles.galleryContainer} {...protectImages}>
              {hasFeatured && (
                <>
                  <SectionDivider title={featuredSection.name} count={FEATURED.length} first />
                  {renderGrid(FEATURED, 0)}
                  <SectionDivider title={t("gallery.all_pictures")} count={sourceImages.length} />
                </>
              )}
              {renderGrid(IMAGES, hasFeatured ? FEATURED.length : 0)}
              <div ref={loaderRef} style={{ height: "20px" }} />
            </div>
          </div>

          {openLBox && (
            <Lightbox
              open={openLBox}
              close={() => setOpenLBox(false)}
              slides={LB_ITEMS.map((image) => ({ src: allowDownload ? img_url(image.name) : img_url(image.name, true, true) }))}
              {...(allowDownload ? {} : { render: { slideContainer: ({ children }) => <div {...protectImages} style={{ ...protectImages.style, width: "100%", height: "100%" }}>{children}</div> } })}
              index={selectedIndex}
              onIndexChange={setSelectedIndex}
            />
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
          <div style={{ width: 52, height: 52, borderRadius: "14px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <FaRegImages style={{ fontSize: "22px", color: "#d1d5db" }} />
          </div>
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>No images yet</div>
          <div style={{ fontSize: "12px" }}>Upload photos to get started</div>
        </div>
      )}

      {/* Download or Preview */}
      <Modal
        isOpen={isPicModalOpen}
        onClose={() => setPicModalOpen(false)}
        title={images[selectedIndex] ? images[selectedIndex].name + " (" + getImageHumanSize(images[selectedIndex].size) + ")" : ""}
      >
        <div style={{ display: "flex", justifyContent: "center", gap: "12px", padding: "8px" }}>
          {allowDownload && (
            <button
              style={{ display: "flex", alignItems: "center", gap: "7px", padding: "10px 20px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
              onClick={() => images[selectedIndex] && handleDownloadImage(img_url(images[selectedIndex].name))}
            >
              <AiOutlineDownload style={{ fontSize: "16px" }} /> Download
            </button>
          )}
          <button
            style={{ display: "flex", alignItems: "center", gap: "7px", padding: "10px 20px", borderRadius: "10px", border: "1px solid #c7d2fe", background: "#eef2ff", color: "#6366f1", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
            onClick={() => setOpenLBox(true)}
          >
            <FaRegImages style={{ fontSize: "14px" }} /> Preview
          </button>
        </div>
      </Modal>

      {/* Login prompt for unauthenticated interaction attempts */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
      />

      {/* Share modal */}
      <ShareModal
        isOpen={!!shareModalImage}
        onClose={() => setShareModalImage(null)}
        shareUrl={shareModalImage ? `${process.env.REACT_APP_MODEL_ENDPOINT}/s/${event.id_name}/${shareModalImage}` : ""}
        onShareToEventPage={() => {
          const name = shareModalImage;
          setShareModalImage(null);
          setGuestGalleryShareImage(name);
        }}
      />

      {/* Event Wall share modal — caption + tags */}
      <GuestGalleryShareModal
        isOpen={!!guestGalleryShareImage}
        onClose={() => setGuestGalleryShareImage(null)}
        imageName={guestGalleryShareImage}
        imageUrl={guestGalleryShareImage ? img_url(guestGalleryShareImage, true) : ""}
        eventId={event?.event_id}
      />

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <>
          <div
            onClick={() => setDeleteConfirm(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1050 }}
          />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            background: "#fff", borderRadius: 14,
            boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
            zIndex: 1055, width: "min(90vw, 380px)", padding: "24px",
          }}>
            <h6 style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>
              {t("gallery_delete.title")}
            </h6>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.5 }}>
              {deleteConfirm.type === "selected"
                ? t("gallery_delete.confirm_selected", { count: selectedImages.length })
                : t("gallery_delete.confirm_all")}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: "7px 16px", borderRadius: 8, border: "1px solid #e5e7eb",
                  background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer",
                }}
              >
                {t("gallery_delete.cancel")}
              </button>
              <button
                onClick={executeDelete}
                style={{
                  padding: "7px 16px", borderRadius: 8, border: "1px solid #fca5a5",
                  background: "#fee2e2", color: "#ef4444", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <AiOutlineDelete style={{ fontSize: 13 }} />
                {t("gallery_delete.confirm_action")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Album modal */}
      {showAlbumModal && (
        <AlbumModal
          isOpen={showAlbumModal}
          onClose={() => setShowAlbumModal(false)}
          event={event}
          preSelectedImages={selectedImages}
          onAlbumCreated={() => {
            onAlbumCreated && onAlbumCreated();
          }}
          onAlbumOpen={onAlbumChange}
          onNeedSelection={handleNeedSelection}
        />
      )}

      {/* Gallery size modal */}
      <GallerySizeModal
        isOpen={showGallerySizeModal}
        onClose={() => setShowGallerySizeModal(false)}
        columns={galleryColumns}
        onChange={handleColumnsChange}
        isMobile={isMobile}
      />
    </div>
  );
};

export default ImageGallery;
